"use strict";
/* eslint-disable camelcase */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createValuationTracker = void 0;
const common_1 = require("@finley-technologies/common");
const createValuationTracker = ({ meshReceivables, maps, }) => {
    // clone receivablesToLoanAccounts, we are going to mutate it.
    const receivablesToRemainingLoanAccounts = new Map(maps.receivableToLoanAccounts);
    const receivableToValuationTracking = new Map();
    // since receivables are shared between loans, we split them into multiple receivables.
    // with their valuation rate sum to 1.
    // we need to keep track of remaining valuation rate for each receivable.
    meshReceivables.forEach((rec) => {
        receivableToValuationTracking.set(rec.finleyReceivableId, {
            remainingValuation: 1,
            currentValuation: 0, // we compute with full valuation to start with, and adjust it later.
        });
        return rec;
    });
    const updateBaseFields = (baseFields, loanAccount, newValuationRate) => {
        const calcAgentFields = baseFields;
        let { finley_valuation_rate, loan_account_number } = calcAgentFields;
        (0, common_1.finleyAssert)(typeof finley_valuation_rate === 'number', `finley_valuation_rate is not a number:[${baseFields.finley_valuation_rate}]`);
        (0, common_1.finleyAssert)(newValuationRate >= 0 && newValuationRate <= 1, `newValuationRate[${newValuationRate}] must be between 0 and 1`);
        finley_valuation_rate = newValuationRate;
        loan_account_number = loanAccount;
        // use typesafe update
        return {
            ...baseFields,
            finley_valuation_rate,
            loan_account_number,
        };
    };
    const updateSyntheticBaseFields = (receivable) => {
        const baseFields = receivable.baseFields;
        baseFields.pre_allocation_lendable_value =
            receivable.requiredIntermediateFields.exposure *
                receivable.borrowingBaseFields.advanceRate;
        baseFields.post_allocation_lendable_value =
            baseFields.pre_allocation_lendable_value *
                baseFields.finley_valuation_rate;
        return receivable;
    };
    // this function is used to find valuation rate adjustments
    // valuation rate is adjusted such that it just covers the loanBalance
    const findAdjustedValuationRate = ({ rec, loanBalance, }) => {
        const { borrowingBaseAmount } = rec.borrowingBaseFields;
        const { valuationRate } = rec.valuationFields;
        // at this point, the valuationRate is 1 or less
        // and borrowingBaseAmount is based on the valuationRate.
        const usedBorrowingBase = Math.min(loanBalance, borrowingBaseAmount);
        const remainingLoanBalance = loanBalance - usedBorrowingBase;
        const useFactor = common_1.utils.divideByOrZero(usedBorrowingBase, borrowingBaseAmount);
        const suggestedValuationRate = Math.max(0, Math.min(valuationRate * useFactor, 1));
        return {
            suggestedValuationRate: Math.max(0, Math.min(suggestedValuationRate, 1)),
            remainingLoanBalance,
        };
    };
    const updateValuationRate = ({ rec, loanBalance, finleyReceivableId, }) => {
        // round up loan balance to 2 decimal places
        // this will ensure that we dont under allocate for the loan
        // with floating point rounding issues in calc-agent.
        //    with this 1000.23 stays 1000.23
        //    but 1000.234 becomes 1000.24
        // eslint-disable-next-line no-param-reassign
        loanBalance = Math.ceil(loanBalance * 100) / 100;
        const { suggestedValuationRate, remainingLoanBalance, } = findAdjustedValuationRate({ rec, loanBalance });
        const valuationTracking = receivableToValuationTracking.get(finleyReceivableId);
        (0, common_1.finleyAssert)(valuationTracking, `trackingValuation not found for receivableId:[${finleyReceivableId}]`);
        const currentDelta = valuationTracking.currentValuation - suggestedValuationRate;
        valuationTracking.currentValuation -= currentDelta;
        valuationTracking.remainingValuation += currentDelta;
        return remainingLoanBalance;
    };
    const getReceivablesForLoanAccount = ({ loanAccount, useValuation, }) => {
        const loanAccountReceivableIds = maps.loanAccountToReceivables.get(loanAccount) || [];
        const meshReceivablesForLoanAccount = meshReceivables
            .filter((rec) => loanAccountReceivableIds.includes(rec.finleyReceivableId))
            .sort((a, b) => {
            // sort receivables by exposure (least to most)
            // receivables with least expoure will be used first.
            const exposureDiff = a.requiredIntermediateFields.exposure -
                b.requiredIntermediateFields.exposure;
            if (exposureDiff !== 0) {
                return exposureDiff;
            }
            // if both receivables have same exposure, sort by finleyReceivableId
            return a.finleyReceivableId.localeCompare(b.finleyReceivableId);
        })
            .map((rec) => {
            const valuationTracking = receivableToValuationTracking.get(rec.finleyReceivableId);
            (0, common_1.finleyAssert)(valuationTracking, 'valuationTracking not found');
            if (useValuation === 'available') {
                valuationTracking.currentValuation +=
                    valuationTracking.remainingValuation;
                valuationTracking.remainingValuation = 0;
                receivableToValuationTracking.set(rec.finleyReceivableId, valuationTracking);
            }
            const baseFields = updateBaseFields(rec.baseFields, loanAccount, valuationTracking.currentValuation);
            return {
                ...rec,
                baseFields,
            };
        });
        return meshReceivablesForLoanAccount;
    };
    const finalizeValuations = (receivables) => {
        // now that valuations are finalized
        // we can empty the current valuations.
        receivableToValuationTracking.forEach((tracking) => {
            // eslint-disable-next-line no-param-reassign
            tracking.currentValuation = 0;
        });
        // update the pre_allocation_lendable_value and post_allocation_lendable_value
        return receivables.map((rec) => updateSyntheticBaseFields(rec));
    };
    return {
        getReceivablesForLoanAccount,
        // once final pass is done, we empty any `current` valuations
        finalizeValuations,
        // update current valuations using the borrowingBaseReceivables results
        // + loanBalance.
        updateCurrentValuationRates: ({ borrowingBaseReceivables, loanAccount, loanBalance, }) => {
            // 1st use any receivables that are not needed anymore.
            let remainingLoanBalance = loanBalance;
            borrowingBaseReceivables.forEach((rec) => {
                const { borrowingBaseAmount } = rec.borrowingBaseFields;
                const loanAccountsForReceivable = receivablesToRemainingLoanAccounts.get(rec.finleyReceivableId);
                // remove current loanAccount from the list.
                const remainingLoanAccountsForReceivable = loanAccountsForReceivable.filter((la) => la !== loanAccount);
                if (remainingLoanAccountsForReceivable.length === 0) {
                    // this receivable is not going to be used by any other loan account.
                    // use full borrowing base against remainingLoanBalance.
                    receivablesToRemainingLoanAccounts.delete(rec.finleyReceivableId);
                    remainingLoanBalance = Math.max(remainingLoanBalance - borrowingBaseAmount, 0);
                }
                else {
                    // we will adjust the valuation rate for the receivable.
                    // in 2nd pass below.
                    receivablesToRemainingLoanAccounts.set(rec.finleyReceivableId, remainingLoanAccountsForReceivable);
                }
            });
            // at this point we have accounted for all the receivables that were
            // exclusive for the loan account.
            // Rest of the receivables are shared between loan account.
            // We want to adjust the valuation of these receviables to match the remaining balance.
            // and carry forward any remaining valuation for the next loan.
            borrowingBaseReceivables.forEach((rec) => {
                const { finleyReceivableId } = rec;
                if (receivablesToRemainingLoanAccounts.has(finleyReceivableId)) {
                    // this receivable is going to be used by another run
                    // we need to adjust the valuationRate to match the required exposure.
                    remainingLoanBalance = updateValuationRate({
                        rec,
                        loanBalance: remainingLoanBalance,
                        finleyReceivableId,
                    });
                }
            });
        },
    };
};
exports.createValuationTracker = createValuationTracker;
//# sourceMappingURL=valuationTracker.js.map